import { batch } from 'react-redux';
import { DataStore, Predicates } from 'aws-amplify/datastore';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  AttachedFile,
  Bulletin,
  CustomFieldUsage,
  CustomDeck,
  CustomFormRecord,
  Document,
  DocumentVersion,
  EmailTemplate,
  EmailTemplateStatus,
  Folder,
  Hub,
  Meeting,
  PromptValues,
  PromptStatus,
  Tenant,
  User,
  UserDocumentVersionSettings,
  UserNotations,
  UserSettings,
  UserRole,
  UserStatus,
} from '@alucio/aws-beacon-amplify/src/models';

import { store } from 'src/state/redux';
import { bulletinActions } from 'src/state/redux/slice/bulletin';
import { attachedFileActions } from 'src/state/redux/slice/attachedFile';
import { promptValuesActions } from 'src/state/redux/slice/promptValues';
import { customDeckActions } from 'src/state/redux/slice/customDeck';
import { customFormRecordActions } from 'src/state/redux/slice/customFormRecord';
import { documentActions } from 'src/state/redux/slice/document';
import { documentVersionActions } from 'src/state/redux/slice/documentVersion';
import { emailTemplateActions } from 'src/state/redux/slice/emailTemplate';
import { folderActions } from 'src/state/redux/slice/folder';
import { hubActions } from 'src/state/redux/slice/hub';
import { meetingActions } from 'src/state/redux/slice/meeting';
import { tenantActions } from 'src/state/redux/slice/tenant';
import { userActions } from 'src/state/redux/slice/user';
import { userDocumentVersionSettingsActions } from 'src/state/redux/slice/userDocumentVersionSettings';
import { userNotationsActions } from 'src/state/redux/slice/userNotations';
import { userSettingsActions } from 'src/state/redux/slice/userSetting';
import { permissionsSliceActions } from 'src/state/redux/slice/permissions';
import { allCustomDecks } from 'src/state/redux/selector/folder';
import {
  filterDocumentsForDeletedAndLockedFilters,
  getIndexedValidCustomFields,
  matchesLockedFilters,
} from 'src/state/datastore/query';
import activeUser from 'src/state/global/ActiveUser';
import PWALoaded from 'src/state/global/PWALoaded';
import { syncCacheManifest } from 'src/state/cache/syncManifests';
import { LDClient } from 'launchdarkly-react-client-sdk';
import * as logger from 'src/utils/logger';
import {
  upgradeFolderDocuments,
  upgradeLibraryDocumentsNotations,
} from 'src/state/redux/saga';
import { SessionStatus } from 'src/components/IdleComponent/IdleComponent';
import { getFeatureFlagValue, initLDFeatureFlagValues } from 'src/state/redux/selector/permissions';
import { isRequiredField } from 'src/state/redux/selector/common';
import { FeatureFlagConsts } from 'src/types/featureFlags';

const hydrate = async (
  isOfflineEnabled: boolean,
  ldClient: LDClient,
  logout: (reason: SessionStatus | undefined, fromChannel: boolean) => Promise<void>,
  upsert: boolean = false,
  enableOfflineMode?: boolean,
) => {
  logger.clientState.hydrate.info('Starting hydration process');

  // 1. Fetch all DataStore records
  const modelRecords = await Promise.all([
    DataStore.query(Bulletin, Predicates.ALL),
    DataStore.query(AttachedFile, Predicates.ALL),
    DataStore.query(CustomDeck, Predicates.ALL),
    DataStore.query(CustomFormRecord, Predicates.ALL),
    DataStore.query(Document, Predicates.ALL),
    DataStore.query(DocumentVersion, Predicates.ALL),
    DataStore.query(EmailTemplate, Predicates.ALL),
    DataStore.query(Folder, Predicates.ALL),
    DataStore.query(Hub, Predicates.ALL),
    DataStore.query(Meeting, Predicates.ALL),
    DataStore.query(PromptValues, Predicates.ALL),
    DataStore.query(Tenant, Predicates.ALL),
    DataStore.query(User, Predicates.ALL),
    DataStore.query(UserDocumentVersionSettings, Predicates.ALL),
    DataStore.query(UserNotations, Predicates.ALL),
    DataStore.query(UserSettings, Predicates.ALL),
  ]);

  logger.clientState.hydrate.info('DataStore queries completed');

  const [
    bulletins,
    attachedFiles,
    customDeck,
    customFormRecords,
    documents,
    documentVersions,
    emailTemplates,
    folders,
    hubs,
    meetings,
    promptValues,
    tenants,
    users,
    allUserDocumentVersionSettings,
    allUserNotations,
    userSettings,
  ] = modelRecords;

  // 2. Attempt to identify CognitoUser
  // [TODO] - Type this if possible, but not officially provided definition
  const data = await fetchAuthSession();
  const cognitoUserId = data.tokens?.idToken?.payload['custom:user_id']

  /**
   * first cognitoUser.signInUserSession.idToken.payload.email is the one that we are overriding on the cognitoTriggerHandler that one is in lowercase
   * second cognitoUser.attributes.email is the one that we are getting from the cognitoUserPool that one can be in uppercase or lowercase
   */
  // const cognitoEmail = cognitoUser.email;
  const currentUser = users.find(({ id }) => id === cognitoUserId);

  if (!currentUser) {
    logger.clientState.hydrate.error('User not found in DynamoDB');
    throw new Error('User not found in DynamoDB');
  }

  if (currentUser.status === UserStatus.DEACTIVATED) {
    logger.clientState.hydrate.warn('User is DEACTIVATED, forcing logout');
    await logout(SessionStatus.deactivated, false);
    throw new Error('User is DEACTIVATED');
  }

  // 3. Set Singleton values
  activeUser.set(currentUser)
  const isPWALoaded = window.matchMedia('(display-mode: standalone)').matches
  PWALoaded.set(isPWALoaded)

  // 4. Ensure there's only one Tenant Record loaded from DataStore
  if (tenants.length > 1 && currentUser?.role !== UserRole.ALUCIO_ADMIN) {
    logger.clientState.hydrate.error('More than one tenant returned by Datastore');
    throw new Error('More than one tenant returned by Datastore');
  }
  const usersTenant = tenants.find(({ id }) => id === currentUser?.tenantId);

  // 5. Apply filters for DocVer records going into Redux
  //    - Locked Filters
  //    - Deleted Docs
  const validIndexedCustomFields = getIndexedValidCustomFields(usersTenant?.config?.customFields || []);
  const validEmailTemplateCustomFields = getIndexedValidCustomFields(
    usersTenant?.config?.customFields || [],
    CustomFieldUsage.EMAIL_TEMPLATE_FILTER,
  );
  const { documents: modifiedDocuments, versions: modifiedDocumentVersions } =
    filterDocumentsForDeletedAndLockedFilters(
      currentUser,
      documents,
      documentVersions,
      validIndexedCustomFields,
    )

  // 6. Apply filters for EmailTemplate records going into Redux
  //    - Locked filters
  //    - Deleted Templates
  const modifiedEmailTemplates = emailTemplates
    .filter(({ customFilterValues, status }) => (
      matchesLockedFilters(
        currentUser?.lockedFiltersCustomValues || [],
        validEmailTemplateCustomFields,
      )(status === EmailTemplateStatus.ACTIVE, customFilterValues) &&
      status !== 'REMOVED'
    ));

  // 7. Apply filters for user-scoped settings records going into Redux
  // [NOTE] - We currently do not update this status
  const filteredUserDocumentVersionSettings =
    allUserDocumentVersionSettings.filter(settings => settings.status !== 'DELETED');

  // 8. Apply filters for UserNotation records going into Redux
  // [NOTE] - We currently do not update this status
  const filteredUserNotations = allUserNotations.filter(userNotations => userNotations.status !== 'DELETED');
  // 9. Apply filters for Folder records going into Redux
  const filteredFolder = folders.filter(folder => folder.status !== 'REMOVED');
  // 10. Apply filters for CustomDeck records going into Redux
  // [TODO] - We should filter these out at the DataStore query level
  const filteredCustomDeck: CustomDeck[] = [];
  filteredFolder.forEach(folder => {
    folder.items.forEach(folderItem => {
      if (folderItem.status === 'ACTIVE' && folderItem.type === 'CUSTOM_DECK') {
        const targetCustomDeck = customDeck.find(cd => cd.id === folderItem.itemId);
        if (targetCustomDeck) filteredCustomDeck.push(targetCustomDeck);
      }
    })
  });
  // 11. Apply filters for Hub records going into Redux
  const filteredHubs = hubs.filter(hub => hub.status !== 'DELETED');

  // 12. Apply filters for PromptValues records going into Redux
  const filteredPromptValues = promptValues.filter(prompt => prompt.status === PromptStatus.ACTIVE);

  // 13. Setup Analytics (based on current user)
  let ldFlags: FeatureFlagConsts | undefined

  if (currentUser) {
    const userInfo = {
      userId: currentUser.id,
      email: currentUser.email,
      givenName: currentUser.givenName,
      lastName: currentUser.familyName,
      role: [currentUser.role],
      tenant: usersTenant?.name,
      tenantId: currentUser.tenantId,
    }
    logger.clientState.hydrate.info('Initializing analytics', userInfo);
    analytics?.identify(currentUser.email, userInfo);

    logger.clientState.hydrate.info('Setting group properties on analytics', userInfo);
    analytics?.group(currentUser.tenantId, {
      name: usersTenant?.name,
    });

    const newContext = {
      kind: 'user',
      // there is also the option to have a multi context with user and tenant, but if we decided to create a tenant context and use that we would have to refactor all of our existing flags in the LD dashboard
      key: currentUser.id,
      tenantId: currentUser.tenantId,
      tenantName: usersTenant?.name,
    }

    // 14. Setup LaunchDarkly (based on current user)
    // [NOTE] - We skip LD Client errors for now (this should normally error in offline mode)
    logger.clientState.hydrate.info('Configuring LaunchDarkly');
    await ldClient
      ?.identify(newContext)
      .catch((error) => {
        logger.clientState.hydrate.warn('LD Client identify failed', error);
      })

    // 15. Sync last known LD flags into Redux
    // [NOTE] - This will not update LD flag values in realtime, which is not necessary for our usage
    //        - However, it wouldn't be too hard to have a hook that syncs using the LD hook to dispatch updates to Redux
    ldFlags = initLDFeatureFlagValues(ldClient)
  }

  // 16. Insert (filtered) records into Redux
  batch(() => {
    // DataStore
    logger.clientState.hydrate.info('Dispatching data to Redux store')
    // [NOTE]: Not typed so be careful here
    const action = upsert ? 'batchUpsert' : 'add'

    store.dispatch(bulletinActions[action](bulletins))
    store.dispatch(attachedFileActions[action](attachedFiles))
    store.dispatch(promptValuesActions[action](filteredPromptValues))
    store.dispatch(customDeckActions[action](filteredCustomDeck))
    store.dispatch(customFormRecordActions[action](customFormRecords))
    store.dispatch(documentActions[action](modifiedDocuments))
    store.dispatch(documentVersionActions[action](modifiedDocumentVersions))
    store.dispatch(emailTemplateActions[action](modifiedEmailTemplates))
    store.dispatch(folderActions[action](filteredFolder))
    store.dispatch(hubActions[action](filteredHubs))
    store.dispatch(meetingActions[action](meetings))
    store.dispatch(tenantActions[action](tenants))
    store.dispatch(userActions[action](users))
    store.dispatch(userDocumentVersionSettingsActions[action](filteredUserDocumentVersionSettings))
    store.dispatch(userNotationsActions[action](filteredUserNotations))
    store.dispatch(userSettingsActions[action](userSettings))

    if (ldFlags) {
      store.dispatch(permissionsSliceActions.add(ldFlags))
      store.dispatch(permissionsSliceActions.setHydrated())
    }

    // Mark as hydrated
    // [TODO] - Alternatively, manage an overall app hydrated state in a separate slice
    store.dispatch(bulletinActions.setHydrated())
    store.dispatch(attachedFileActions.setHydrated())
    store.dispatch(promptValuesActions.setHydrated())
    store.dispatch(customDeckActions.setHydrated())
    store.dispatch(customFormRecordActions.setHydrated())
    store.dispatch(documentActions.setHydrated())
    store.dispatch(documentVersionActions.setHydrated())
    store.dispatch(emailTemplateActions.setHydrated())
    store.dispatch(folderActions.setHydrated())
    store.dispatch(hubActions.setHydrated())
    store.dispatch(meetingActions.setHydrated())
    store.dispatch(tenantActions.setHydrated())
    store.dispatch(userActions.setHydrated())
    store.dispatch(userDocumentVersionSettingsActions.setHydrated())
    store.dispatch(userNotationsActions.setHydrated())
    store.dispatch(userSettingsActions.setHydrated())

    // Now that everything's hydrated we need to check if we need to auto-update anything
    // NOTE: Including validation for null because that whats we get instead of undefined if the
    // field does not exist in DynamoDB Tenant record
    if (
      usersTenant?.folderUpdateGracePeriodDays !== undefined &&
      usersTenant?.folderUpdateGracePeriodDays !== null
    ) {
      // 14. Auto-upgrade Custom Deck Minor Versions
      try {
        const customDecks = allCustomDecks(store.getState())
        store.dispatch(customDeckActions.autoUpgradeCustomDecks({
          customDeckORMs: customDecks,
          gracePeriodDays: usersTenant?.folderUpdateGracePeriodDays ?? 0,
        }))
      } catch (error) {
        logger.clientState.hydrate.error('Failed to auto-upgrade custom decks:', error);
      }
    }
  })

  try {
    // 15. Apply Auto upgrades - Library Document Version Notations
    await upgradeLibraryDocumentsNotations()
  } catch (error) {
    logger.clientState.hydrate.error('Failed to auto-upgrade library document version notations:', error);
  }
  try {
    // 16. Apply Auto upgrades - Bump DocVers in Folders
    await upgradeFolderDocuments()
  } catch (error) {
    logger.clientState.hydrate.error('Failed to auto-upgrade folder document versions:', error);
  }

  const userPermissionFields = (usersTenant?.config?.customFields ?? []).filter(customField =>
    isRequiredField(customField, { internalUsages: [CustomFieldUsage.USER_FILTER] }),
  );
  const offlineModeEnabled = enableOfflineMode ?? getFeatureFlagValue(
    'enableOfflineMode',
    ldFlags ?? {} as FeatureFlagConsts,
    usersTenant,
    currentUser.role,
    currentUser,
    userPermissionFields,
  );

  // 17. Prepare Offline Cache syncing
  if (isOfflineEnabled && offlineModeEnabled) {
    logger.offline.contentCache.info('Syncing cache manifest for offline mode');
    await syncCacheManifest();
  }

  logger.clientState.hydrate.info('Hydration process completed');
}

export default hydrate;
